import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getStaffsAPI,
  createStaffAPI,
  updateStaffAPI,
  deleteStaffAPI,
  changeStaffPasswordAPI,
} from "../../../services/portal/admin/staffService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import RemoveConfirmationDialog from "../../../components/portal/RemoveConfirmationDialog";
import { tableHeaderColor } from "../../../utility/Common";

const StaffManagePage = () => {
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [staffs, setStaffs] = useState([]);
  const [editingDetails, setEditingDetails] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);
  const [changePasswordItem, setChangePasswordItem] = useState(null);
  const [changingPassword, setChangingPassword] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    mobileNumber: "",
    nic: "",
    password: "",
    confirmPassword: "",
  });
  const [changePasswordData, setChangePasswordData] = useState({
    password: "",
    confirmPassword: "",
  });

  useEffect(() => {
    loadStaffs();
  }, []);

  const loadStaffs = async () => {
    setLoading(true);
    try {
      const res = await getStaffsAPI();
      if (res && res.IsSuccessful && res.Data) {
        setStaffs(res.Data);
      } else {
        setStaffs([]);
      }
    } catch (error) {
      console.error("Error loading staffs:", error);
      showErrorToastMsg("Failed to load staffs");
      setStaffs([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleChangePasswordChange = (e) => {
    const { name, value } = e.target;
    setChangePasswordData({
      ...changePasswordData,
      [name]: value,
    });
  };

  const validateForm = () => {
    if (!formData.firstName.trim()) {
      showErrorToastMsg("Please enter staff first name");
      return false;
    }

    if (!formData.lastName.trim()) {
      showErrorToastMsg("Please enter staff last name");
      return false;
    }

    if (!formData.mobileNumber.trim()) {
      showErrorToastMsg("Please enter mobile number");
      return false;
    }

    if (!formData.nic.trim()) {
      showErrorToastMsg("Please enter NIC");
      return false;
    }

    if (!/^[0-9+\-\s()]+$/.test(formData.mobileNumber)) {
      showErrorToastMsg("Please enter a valid mobile number");
      return false;
    }

    if (!editingDetails) {
      // Only validate password for new staff
      if (!formData.password) {
        showErrorToastMsg("Please enter password");
        return false;
      }

      if (formData.password.length < 8) {
        showErrorToastMsg("Password must be at least 8 characters long");
        return false;
      }

      if (formData.password !== formData.confirmPassword) {
        showErrorToastMsg("Passwords do not match");
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const params = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        mobileNo: formData.mobileNumber.trim(),
        nic: formData.nic.trim(),
      };

      if (editingDetails) {
        params.userId = editingDetails.UserId;
        const res = await updateStaffAPI(params);
        if (res && res.IsSuccessful) {
          showSuccessToastMsg(res.Message || "Staff updated successfully");
          handleCancelEdit();
          loadStaffs();
        } else {
          showErrorToastMsg(
            res?.Message || "Something went wrong, Please try again later",
          );
        }
      } else {
        params.password = formData.password;
        const res = await createStaffAPI(params);
        if (res && res.IsSuccessful) {
          showSuccessToastMsg(res.Message || "Staff created successfully");
          clearFields();
          loadStaffs();
        } else {
          showErrorToastMsg(
            res?.Message || "Something went wrong, Please try again later",
          );
        }
      }
    } catch (error) {
      console.error("Error saving staff:", error);
      showErrorToastMsg("Failed to save staff");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDetails = (staffItem) => {
    setEditingDetails(staffItem);
    setFormData({
      firstName: staffItem.FirstName,
      lastName: staffItem.LastName,
      mobileNumber: staffItem.MobileNo,
      nic: staffItem.NIC,
      password: "",
      confirmPassword: "",
    });
  };

  const handleCancelEdit = () => {
    setEditingDetails(null);
    clearFields();
  };

  const clearFields = () => {
    setFormData({
      firstName: "",
      lastName: "",
      mobileNumber: "",
      nic: "",
      password: "",
      confirmPassword: "",
    });
  };

  const handleDelete = (staff) => {
    setDeleteItem(staff);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      const staffId = deleteItem.UserId;
      const res = await deleteStaffAPI(staffId);

      if (res.IsSuccessful) {
        loadStaffs();
        showSuccessToastMsg("Staff deleted successfully");
      } else {
        showErrorToastMsg(res.Message || "Failed to delete staff");
      }

      setShowDeleteConfirm(false);
      setDeleteItem(null);
    } catch (error) {
      console.error("Error deleting staff:", error);
      showErrorToastMsg("Failed to delete staff");
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteItem(null);
  };

  const handleChangePassword = (staff) => {
    setChangePasswordItem(staff);
    setChangePasswordData({
      password: "",
      confirmPassword: "",
    });
    setShowChangePasswordModal(true);
  };

  const handleCancelChangePassword = () => {
    setShowChangePasswordModal(false);
    setChangePasswordItem(null);
    setChangePasswordData({
      password: "",
      confirmPassword: "",
    });
  };

  const handleSubmitChangePassword = async (e) => {
    e.preventDefault();

    if (!changePasswordData.password) {
      showErrorToastMsg("Please enter password");
      return;
    }

    if (changePasswordData.password.length < 8) {
      showErrorToastMsg("Password must be at least 8 characters long");
      return;
    }

    if (changePasswordData.password !== changePasswordData.confirmPassword) {
      showErrorToastMsg("Passwords do not match");
      return;
    }

    setChangingPassword(true);

    try {
      const staffId = changePasswordItem.UserId;
      const params = {
        userId: staffId,
        password: changePasswordData.password,
      };

      const res = await changeStaffPasswordAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Password changed successfully");
        handleCancelChangePassword();
      } else {
        showErrorToastMsg(res?.Message || "Failed to change password");
      }
    } catch (error) {
      console.error("Error changing password:", error);
      showErrorToastMsg("Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <DashboardLayout title="Staff Management" subtitle="Manage staff members">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-6">Manage Staff</h3>

        {/* Add/Edit Form */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">
              {editingDetails ? "Edit Staff" : "Add New Staff"}
            </h4>
          </div>
          <div className="p-6">
            <form
              onSubmit={handleSubmit}
              className="grid grid-cols-1 md:grid-cols-2 gap-4"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  placeholder="Enter staff first name"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  placeholder="Enter staff last name"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mobile Number <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  NIC <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="nic"
                  value={formData.nic}
                  onChange={handleChange}
                  placeholder="Enter mobile number"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                />
              </div>

              {!editingDetails && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Enter password (min. 8 characters)"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required={!editingDetails}
                      minLength={8}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                      required={!editingDetails}
                      minLength={8}
                    />
                  </div>
                </>
              )}

              <div className="flex gap-2 items-end justify-end md:col-span-2">
                {editingDetails && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="text-sm font-medium px-4 py-1.5 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={saving}
                  className={`text-md font-medium px-4 py-1.5 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving
                    ? "Saving..."
                    : editingDetails
                      ? "Update"
                      : "Add Staff"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Staffs Table */}
        <div className="bg-white border border-gray-200 rounded-lg mb-6 overflow-hidden">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">Staff List</h4>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    First Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mobile Number
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      <Loader />
                    </td>
                  </tr>
                ) : staffs.length === 0 ? (
                  <tr>
                    <td
                      colSpan="3"
                      className="text-sm font-medium px-6 py-4 text-center text-gray-500"
                    >
                      No staff found.
                    </td>
                  </tr>
                ) : (
                  staffs.map((staffItem) => (
                    <tr key={staffItem.UserId} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {staffItem.FirstName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {staffItem.LastName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {staffItem.MobileNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex gap-4">
                          <button
                            onClick={() => handleEditDetails(staffItem)}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleChangePassword(staffItem)}
                            className="text-green-600 hover:text-green-900"
                          >
                            Change Password
                          </button>
                          <button
                            onClick={() => handleDelete(staffItem)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete Confirmation Dialog */}
        {showDeleteConfirm && (
          <RemoveConfirmationDialog
            loading={loading}
            description={deleteItem?.Name || deleteItem?.name}
            cancelDelete={cancelDelete}
            confirmDelete={confirmDelete}
          />
        )}

        {/* Change Password Modal */}
        {showChangePasswordModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0">
                  <svg
                    className="h-6 w-6 text-primary-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-lg font-medium text-gray-900">
                    Change Password
                  </h3>
                </div>
              </div>

              <form onSubmit={handleSubmitChangePassword}>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-4">
                    Change password for:{" "}
                    <span className="font-medium text-gray-900">
                      {changePasswordItem?.FirstName +
                        " " +
                        changePasswordItem.LastName}
                    </span>
                  </p>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      New Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={changePasswordData.password}
                      onChange={handleChangePasswordChange}
                      placeholder="Enter new password (min. 8 characters)"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      minLength={8}
                    />
                  </div>

                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Confirm Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={changePasswordData.confirmPassword}
                      onChange={handleChangePasswordChange}
                      placeholder="Confirm new password"
                      className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                      minLength={8}
                    />
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={handleCancelChangePassword}
                    disabled={changingPassword}
                    className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 disabled:opacity-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={changingPassword}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 font-medium"
                  >
                    {changingPassword ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default StaffManagePage;
